import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../../services/apiService';

const PERIODS = [
  { key: 'last24h', label: '24 Hours' },
  { key: 'last7d',  label: '7 Days'   },
  { key: 'last30d', label: '30 Days'  },
];

const verifyBadge = {
  approved:   'bg-emerald-50 text-emerald-700',
  pending:    'bg-yellow-50  text-yellow-700',
  inProgress: 'bg-blue-50    text-blue-700',
  rejected:   'bg-red-50     text-red-700',
};

const fmtTime = (d) => {
  if (!d) return '—';
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60)   return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)   return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const ActiveVendors = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod]   = useState('last7d');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await apiService.get('activeVendors');
        if (res.status) setData(res.data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const list = data?.[period] || [];
  const maxCount = list[0]?.activityCount || 1;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-4">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#009ef7]/10 flex items-center justify-center">
            <i className="bi bi-activity text-[#009ef7] text-sm"></i>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 leading-tight">Most Active Vendors</h3>
            <p className="text-[11px] text-gray-400">Ranked by CRM activity count</p>
          </div>
        </div>

        {/* Period tabs */}
        <div className="flex items-center bg-gray-100 rounded-lg p-0.5 gap-0.5">
          {PERIODS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setPeriod(key)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                period === key
                  ? 'bg-white text-[#009ef7] shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex items-center justify-center py-10">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#009ef7] border-t-transparent"></div>
        </div>
      ) : list.length === 0 ? (
        <div className="text-center py-10">
          <i className="bi bi-person-x text-3xl text-gray-200 block mb-2"></i>
          <p className="text-xs text-gray-400">No activity in this period</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-50">
          {list.map((item, idx) => {
            const v = item.vendor || {};
            const pct = Math.round((item.activityCount / maxCount) * 100);
            return (
              <div key={item.vendorId} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">

                {/* Rank */}
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${
                  idx === 0 ? 'bg-amber-100 text-amber-600' :
                  idx === 1 ? 'bg-slate-100 text-slate-500' :
                  idx === 2 ? 'bg-orange-100 text-orange-500' :
                  'bg-gray-100 text-gray-400'
                }`}>
                  {idx + 1}
                </div>

                {/* Avatar */}
                <div className="w-9 h-9 rounded-full bg-gray-100 flex-shrink-0 overflow-hidden border border-gray-200">
                  {v.image
                    ? <img src={`${process.env.REACT_APP_MEDIA_URL}${v.image}`} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center"><i className="bi bi-person-fill text-gray-300 text-sm"></i></div>
                  }
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <Link
                      to={`/vendor/activity/${item.vendorId}`}
                      className="text-xs font-semibold text-gray-900 hover:text-[#009ef7] truncate no-underline"
                    >
                      {v.business_name || `${v.first_name || ''} ${v.last_name || ''}`.trim() || 'Unknown'}
                    </Link>
                    {v.verify && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${verifyBadge[v.verify] || 'bg-gray-100 text-gray-500'}`}>
                        {v.verify}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-gray-400 truncate">{v.email || '—'}</div>
                  {/* Progress bar */}
                  <div className="mt-1.5 flex items-center gap-2">
                    <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-[#009ef7] transition-all"
                        style={{ width: `${pct}%` }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="flex-shrink-0 text-right">
                  <div className="text-sm font-bold text-gray-900">{item.activityCount}</div>
                  <div className="text-[10px] text-gray-400">actions</div>
                  <div className="text-[10px] text-gray-400 mt-0.5">{fmtTime(item.lastActive)}</div>
                </div>

                {/* Unique actions badge */}
                <div className="flex-shrink-0 hidden sm:flex flex-col items-center gap-1">
                  <div className="px-2 py-0.5 bg-[#009ef7]/8 rounded text-[10px] font-medium text-[#009ef7] whitespace-nowrap">
                    {item.uniqueActions} action types
                  </div>
                  <Link
                    to={`/vendor/activity/${item.vendorId}`}
                    className="text-[10px] text-gray-400 hover:text-[#009ef7] no-underline"
                  >
                    View →
                  </Link>
                </div>

              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ActiveVendors;
